// core
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// campfire
import { PolicyService, LoggerService, AnalyticsService, PartnerBrandService, IntercomService } from '@campfire-nx/api';

// services
import { PolicyDetailsService } from '@open-portal-app/core/services/policy-details.service';
import { AuthService } from '@open-portal-app/core/services/auth.service';
import { UserDataService } from '@open-portal-app/core/services/user-data.service';
import { environment } from '@open-portal-env';
import { TpaService } from '@open-portal-app/core/services/tpa.service';

// pipes
import { AddressPipe } from '@open-portal-app/core/pipes/address.pipe';

// constants
import { HELP_CONSTANTS } from '@open-portal-app/help/constants/help.constants';

@Component({
  selector: 'policy-details-legacy', // legacy view of the policy detail, this will be removed once v2 policy view is completed
  templateUrl: './policy-details.component.html',
  styleUrls: ['./policy-details.component.scss']
})
export class PolicyDetailsLegacyComponent implements OnInit {
  userDataLoading = false;
  isLoadingError = false;
  token!: string;
  backendUrl = environment.apiEndpoint;
  policyDetails!: any;
  claimsData!: any;
  policyDisplayTitle!: string;
  selfService!: string;
  source!: string;
  initialTabState!: number;
  badgeStatus = HELP_CONSTANTS.policyBadgeStatus as any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private policyService: PolicyService,
    private partnerBrandService: PartnerBrandService,
    private policyDetailsService: PolicyDetailsService,
    private authService: AuthService,
    private userDataService: UserDataService,
    private addressPipe: AddressPipe,
    private loggerService: LoggerService,
    private analyticsService: AnalyticsService,
    private intercomService: IntercomService,
    private tpaService: TpaService
  ) {}

  intercomData = this.partnerBrandService.getAttribute('intercom', 'partner');
  isIntercomEnabled = this.intercomData?.intercomAppId;

  ngOnInit(): void {
    this.token = encodeURIComponent(this.authService.getToken() as string);
    this.route.queryParams
      .subscribe(params => {
        this.selfService = params.selfService;
        this.source = params.source;
      });

    if (this.route.snapshot.data.policy) {
      this.userDataLoading = true;
      let currentEventLogic = 'setPolicyDetails';

      this.setPolicyDetails()
        .then(() => {
          currentEventLogic = 'setPolicyDisplayLabel';
          // set the policy display label
          switch (this.policyDetails.productClass) {
            case 'car':
              this.policyDisplayTitle = `${this.policyDetails.carDisplayName} ${this.policyDetails.carRego}`;
              break;
            case 'home':
              this.policyDisplayTitle = this.policyDetails.isRaptorial
                ? this.policyDetails.address
                : (this.addressPipe.transform(this.policyDetails.address) as string);
              break;
            case 'travel':
              this.policyDisplayTitle = this.policyDetails.destinationTitle;
              break;
            case 'mobile':
              this.policyDisplayTitle = this.policyDetails.deviceModel;
              break;
          }
        })
        .then(() => {
          currentEventLogic = 'getClaims';
          this.getClaims();
          this.userDataLoading = false;
        })
        .then(() => {
          currentEventLogic = 'setInitialTabState';
          // setting the initial tab state if from self-service
          this.setInitialTabState();
          this.loggerService.info('PolicyDetailsComponent', 'ngOnInit', { policy: this.policyDetails.slug });
        })
        .catch((err) => {
          this.userDataLoading = false;
          this.isLoadingError = true;
          this.loggerService.error('PolicyDetailsComponent', 'ngOnInit', { error: err }, currentEventLogic);
        });
    }
  }

  setPolicyDetails() {
    return this.policyDetailsService.formatPolicy().then((response: any) => {
      this.policyDetails = response;
    });
  }

  getClaims() {
    return this.userDataService.getUserData().then((response: any) => {
      const { claims } = response;

      this.claimsData = claims.filter((claim: any) => claim.policy_slug === this.route.snapshot.params.uuid);
    });
  }

  setInitialTabState() {
    let tabState = 0;

    if (this.selfService) {
      switch (this.selfService) {
        // toggle second tab for documents
        case 'documents':
          tabState = 1; break;
        default:
          return;
      }
    }

    this.initialTabState = tabState;
  }

  tabClicked(event: any) {
    this.loggerService.info(
      'PolicyDetailsComponent',
      'tabClicked',
      { policy: this.policyDetails.slug, selected_tab: event.tab.textLabel }
    );
  }

  goBack() {
    this.router.navigate([this.source === 'help_flow' ? '/support' : '/dashboard']);
  }

  goToClaims() {
    const singleTpa = this.policyDetails.fnolTPAs?.length === 1;
    const multipleTpa = this.policyDetails.fnolTPAs?.length > 1;
    let doMultipleTpaApply = false;

    if (this.policyDetails.fnolTPAs && this.policyDetails.fnolTPAs.length > 0) {
      // doMultipleTpaApply: Does policy have extras that make multiple TPAs relevant for the policy
      doMultipleTpaApply = this.checkPolicyForIncludedTpaExtras();
    }

    if (this.policyDetails.fnolThirdParty) {
      if (this.policyDetails.fnolViaIntercom && this.isIntercomEnabled) {
        // Open Intercom widget
        this.intercomService.show();
      } else if (singleTpa || !doMultipleTpaApply) {
        // Call Handover API to fetch default TPA url
        this.handoverToTPA('default');
      } else if (multipleTpa && doMultipleTpaApply) {
        // Let customer choose claim TPA type on claim triage page
        this.goToClaimTriage();
      } else {
        this.goToSupport('claim');
      }
    } else {
      this.goToSupport('claim');
    }
  }

  goToSupport(subject?: string) {
    const referenceList = this.policyDetails.slug;

    if (!subject) {
      this.router.navigate(['/support']);
    } else {
      this.router.navigate(['/support'], {
        queryParams: {
          action: 'help_flow',
          subject,
          'reference-list': referenceList,
          'product-class': this.policyDetails.productClass,
          'return-url': encodeURIComponent(window.location.href)
        }
      });
    }
  }

  retryPayment() {
    this.loggerService.info('PolicyDetailsComponent', 'retryPayment', { policy: this.policyDetails.slug }, 'Clicked Retry payment button');
    this.router.navigate([`/policy/payment/retry/${this.policyDetails.slug}`]);
  }



  startThirdPartyUrlClaims(url:string) {
    window.open(url, '_blank');
  }
  startIntercomClaims() {
    if (this.isIntercomEnabled) {
      this.intercomService.show();
    }
  }

  // For Non-legacy policy documents
  downloadDocument(documentType: string) {
    function saveFileClientSide(data: any) {
      let header = data.headers.get('Content-Disposition');
      let fileName = header.split('=')[1] ? header.split('=')[1].replace(/"/g, '') : 'COI';
      let blob = new Blob([data.body], { type: 'application/pdf' });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    }

    switch (documentType) {
      case 'coi':
        this.loggerService.info('PolicyDetailsComponent', 'downloadDocument', { policy: this.policyDetails.slug }, 'Downloaded Certificate of Insurance');
        if (this.policyDetails.isRaptorial) {
          this.policyService.downloadCoi(this.policyDetails.slug)
            .subscribe((data) => {
              saveFileClientSide(data);
            });
        } else {
          // for legacy policies
          this.policyService.downloadLegacyCoi(this.policyDetails.productClass, this.policyDetails.slug)
            .subscribe((data) => {
              saveFileClientSide(data);
            });
        }
        this.analyticsService.trackEvent(
          'Click download COI',
          this.policyDetails.productClass + ' policy',
          'success',
          {
            policy: this.policyDetails.slug,
            source: 'portalV2',
            userSlug: this.policyDetails.userSlug
          }
        );
        break;
      case 'coc':
        this.loggerService.info('PolicyDetailsComponent', 'downloadDocument', { policy: this.policyDetails.slug }, 'Downloaded Certificate of Currency');
        if (this.policyDetails.isRaptorial) {
          this.policyService.downloadCoc(this.policyDetails.slug)
            .subscribe((data) => {
              saveFileClientSide(data);
            });
        } else {
          // for legacy policies
          this.policyService.downloadLegacyCoc(this.policyDetails.productClass, this.policyDetails.slug)
            .subscribe((data) => {
              saveFileClientSide(data);
            });
          this.analyticsService.trackEvent(
            'Click download COC',
            this.policyDetails.productClass + ' policy',
            'success',
            {
              policy: this.policyDetails.slug,
              source: 'portalV2',
              userSlug: this.policyDetails.userSlug
            });
        }
        break;
      case 'pds':
        this.loggerService.info('PolicyDetailsComponent', 'downloadDocument', { policy: this.policyDetails.slug }, 'Downloaded Product Disclosure Statement');
        const pdsDocument = Object.values(this.policyDetails.documents).find((key: any) => key.type === documentType) as any;

        window.open(`${this.backendUrl}${pdsDocument.url}`, '_blank');
        this.analyticsService.trackEvent(
          'Click download PDS',
          this.policyDetails.productClass + ' policy',
          'success',
          {
            policy: this.policyDetails.slug,
            source: 'portalV2',
            userSlug: this.policyDetails.userSlug
          });
        break;
      default:
        return;
    }
  }

  getLatestPolicyDetails() {
    // fetch latest userData for Dashboard if user navigates back
    this.userDataService.getLatestUserData()
      .then(() => {
        // fetch latest policy details
        if (this.policyDetails.isRaptorial) {
          return this.policyDetailsService.getPolicyDetails(this.policyDetails.slug);
        } else {
          return this.policyDetailsService.getLegacyPolicyDetails(this.policyDetails.productClass, this.policyDetails.slug);
        }
      })
      .then((response: any) => {
        return this.policyDetailsService.formatPolicy(response);
      })
      .then((response: any) => {
        this.policyDetails = response;
      });
  }

  displayPaymentsButton() {
    if (this.policyDetails.policyState === 'cancelled') {
      return false;
    } else if (this.policyDetails.productClass === 'travel') {
      return false;
    } else {
      return true;
    }
  }

  handoverToTPA(forCover:string) {
    this.tpaService.handoverToTpa({
      policy_uuid: this.policyDetails.slug,
      for_cover: forCover
    }).then((response: any) => {
      if (response?.url && response?.url !== '') {
        window.open(response?.url, '_blank');
      }
    }
    ).catch((err: any) => {
      this.loggerService.error('PolicyDetailsComponent', 'handoverToTPA', { error: err });
    }
    );
  }

  checkPolicyForIncludedTpaExtras() {
    let numberOfTPAextras = 0;
    for (const tpaItem of this.policyDetails.fnolTPAs) {
      for (const [key, value] of Object.entries<{is_included?: boolean}>(this.policyDetails?.rawExtras || {})) {
        if (tpaItem?.for_cover?.includes(key) && value?.is_included === true) {
            numberOfTPAextras += 1;
        }
      }
    }
    return numberOfTPAextras > 0;
  }

  goToClaimTriage() {
    this.router.navigate([`/policy/${this.policyDetails?.productClass}/${this.policyDetails.slug}/claim-triage`]);
  }
}
